import { useContext, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import { InputTextField } from '@abbott-components/core';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import ControlState from '../../Shared/controlState';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';
import Typeahead from './Typeahead';
import RichTextEditor from './RichTextEditor/EditRichTextEditor';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import formatName from '../formatName';
import isJson from '../../Shared/isJson';
import SizingSelect from '../../Shared/SizingSelect';

export default (control: ControlState) => {
  const { state: workflowEngineState } = useContext(WorkflowEngineContext);

  const { isReadOnlyView } = workflowEngineState;

  const { state } = useContext(WorkflowEngineContext);

  const { workflowType } = state;

  const { schema } = control;

  const properties = schema?.properties as JSONSchema7Properties;

  const { setValue, getValues } = useFormContext();
  const labelFieldName = formatName(properties['label']?.$id as string);
  const stylingLabel = formatName(properties['styling']?.$id as string);

  const [isTypeaheadOpen, setIsTypeaheadOpen] = useState<boolean>(false);
  const [styleValue, setStyleValue] = useState<string>(getValues(stylingLabel));

  const editorData = isJson(getValues(labelFieldName))
    ? getValues(labelFieldName)
    : '';

  const handleAddVariableClick = () => setIsTypeaheadOpen(!isTypeaheadOpen);

  const handleOnTypeAheadOpen = () => setIsTypeaheadOpen(true);

  const handleSelectedOption = (value: string) => {
    if (!value) return;

    const currentValue = JSON.parse(getValues(labelFieldName));

    const contentBlocks = currentValue.blocks;
    const lastEntry = contentBlocks[contentBlocks.length - 1].text;

    const newEntry = `${lastEntry}{${value}}`;
    contentBlocks[contentBlocks.length - 1].text = newEntry;
    currentValue.blocks = contentBlocks;

    setValue(labelFieldName, JSON.stringify(currentValue));
    setIsTypeaheadOpen(false);
  };

  const handleEditorChange = (value: string) => {
    setValue(labelFieldName, value);
  };

  const handleStylingChange = (event: SelectChangeEvent) => {
    setValue(stylingLabel, event.target.value);
    setStyleValue(event.target.value);
  };

  return (
    <Grid container spacing={3}>
      <Grid container item>
        <InputTextField
          name={labelFieldName}
          label="Text"
          isDisabled={isReadOnlyView}
          sx={{ display: 'none' }}
        />
        <RichTextEditor
          initialValue={editorData}
          isReadOnly={isReadOnlyView}
          handleEditorChange={handleEditorChange}
        />
      </Grid>
      <Grid container item alignItems="center" spacing={3}>
        <Grid container item sm={12} spacing={3}>
          <Grid item xs="auto" marginTop="3px">
            <Button
              disabled={isReadOnlyView}
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddVariableClick}
              sx={{
                minHeight: '53px'
              }}
            >
              Add Variable
            </Button>
          </Grid>
          <Grid item xs marginTop="3px">
            <Typeahead
              label="Event Data Options"
              url={`DynamicWorkflowService/EventDataOptions?workflowType=${workflowType}`}
              isDisabled={isReadOnlyView}
              open={isTypeaheadOpen}
              onOpen={handleOnTypeAheadOpen}
              onSelectedOption={handleSelectedOption}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container item spacing={3}>
        <Grid item sm={12} md={4}>
          <InputTextField
            name={formatName(properties['id']?.$id as string)}
            label="ID"
            isDisabled={isReadOnlyView}
          />
        </Grid>
        <Grid item sm={12} md={4} marginTop="16px">
          <FormControl disabled={isReadOnlyView} fullWidth>
            <InputLabel>Styling</InputLabel>
            <Select
              label="Styling"
              value={styleValue}
              onChange={handleStylingChange}
            >
              <MenuItem value={'none'}>None</MenuItem>
              <MenuItem value={'info'}>
                <Alert severity="info">Information</Alert>
              </MenuItem>
              <MenuItem value={'warn'}>
                <Alert severity="warning">Attention</Alert>
              </MenuItem>
            </Select>
          </FormControl>
          <InputTextField
            name={stylingLabel}
            label="Styling"
            sx={{ display: 'none' }}
          />
        </Grid>
        <Grid item sm={12} md={4}>
          <SizingSelect
            isReadOnlyView={isReadOnlyView}
            name={formatName(properties['gridSize']?.$id as string)}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
